import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MessageIcon from '@mui/icons-material/Message';
import utils from '../../universal/utils';
import {
  pickAvatarUpdatedAtMs,
  resolveAvatarSrc,
} from "../../utils/avatarUrl";

const basePath = utils.getEdgeBasePath();

const FriendsModal = ({ open, onClose, friends = [], user }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFriends = friends.filter((friend) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const friendNickname = friend.friendData?.nickname || friend.friendData?.name || '';
    return friendNickname.toLowerCase().includes(query);
  });

  const onlineFriends = filteredFriends.filter((friend) => friend.friendData?.isOnline);
  const offlineFriends = filteredFriends.filter((friend) => !friend.friendData?.isOnline);

  const getAvatarSrc = (friend) => {
    const friendData = friend.friendData;
    return resolveAvatarSrc({
      basePath,
      picture: friendData?.picture,
      userId: friendData?.userId || friendData?._id,
      avatarUpdatedAtMs: pickAvatarUpdatedAtMs(friendData),
      hasCustomAvatar: !!friendData?.avatar,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0b0b0b',
          border: '1px solid rgba(232, 163, 61, 0.3)',
          borderRadius: '12px',
          maxHeight: '80vh',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: '1.5rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#ffffff',
          }}
        >
          Friends
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#ffffff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search Bar */}
      <Box sx={{ padding: '1rem 1.5rem' }}>
        <TextField
          fullWidth
          placeholder="Search friends..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: '#999999', mr: 1 }} />,
            sx: {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              color: '#ffffff',
              '& input': {
                color: '#ffffff',
              },
              '& fieldset': {
                borderColor: 'rgba(232, 163, 61, 0.3)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(232, 163, 61, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#E8A33D',
              },
            },
          }}
        />
      </Box>

      {/* Friends List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: '0 1.5rem 1.5rem',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
            },
          },
        }}
      >
        {friends.length === 0 ? (
          <Box sx={{ textAlign: 'center', padding: '3rem 1rem', color: '#999999' }}>
            <Typography>You have no friends yet</Typography>
            <Typography sx={{ fontSize: '0.85rem', mt: 1 }}>
              Start adding friends to see them here!
            </Typography>
          </Box>
        ) : filteredFriends.length === 0 ? (
          <Box sx={{ textAlign: 'center', padding: '3rem 1rem', color: '#999999' }}>
            <Typography>No friends found</Typography>
            <Typography sx={{ fontSize: '0.85rem', mt: 1 }}>
              Try a different search term
            </Typography>
          </Box>
        ) : (
          <>
            {/* Online Friends */}
            {onlineFriends.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#999999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 1,
                  }}
                >
                  ONLINE - {onlineFriends.length}
                </Typography>
                {onlineFriends.map((friend) => (
                  <Box
                    key={friend.friendSteamId || friend.friendData?._id}
                    component={Link}
                    to={`/profile/${friend.friendData?._id}`}
                    onClick={onClose}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      transition: 'background-color 150ms ease',
                      textDecoration: 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={getAvatarSrc(friend)}
                        sx={{ width: '2.5rem', height: '2.5rem' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#4CAF50',
                          border: '2px solid #0b0b0b',
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          color: '#ffffff',
                        }}
                      >
                        {friend.friendData?.nickname || friend.friendData?.name || 'Unknown'}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#4CAF50',
                        }}
                      >
                        Online
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      sx={{
                        color: '#999999',
                        '&:hover': {
                          color: '#E8A33D',
                          backgroundColor: 'rgba(232, 163, 61, 0.1)',
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // TODO: Open chat/message
                      }}
                    >
                      <MessageIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Offline Friends */}
            {offlineFriends.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#999999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 1,
                  }}
                >
                  OFFLINE - {offlineFriends.length}
                </Typography>
                {offlineFriends.map((friend) => (
                  <Box
                    key={friend.friendSteamId || friend.friendData?._id}
                    component={Link}
                    to={`/profile/${friend.friendData?._id}`}
                    onClick={onClose}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      transition: 'background-color 150ms ease',
                      textDecoration: 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      },
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={getAvatarSrc(friend)}
                        sx={{ width: '2.5rem', height: '2.5rem' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#666666',
                          border: '2px solid #0b0b0b',
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          color: '#ffffff',
                        }}
                      >
                        {friend.friendData?.nickname || friend.friendData?.name || 'Unknown'}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#999999',
                        }}
                      >
                        Offline
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      sx={{
                        color: '#999999',
                        '&:hover': {
                          color: '#E8A33D',
                          backgroundColor: 'rgba(232, 163, 61, 0.1)',
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // TODO: Open chat/message
                      }}
                    >
                      <MessageIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
};

FriendsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  friends: PropTypes.array,
  user: PropTypes.object,
};

export default FriendsModal;
